import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { getDeviceFingerprint } from "@/lib/fingerprint";
import { MydawaLogo } from "@/components/MydawaLogo";
import { Copy, Check, ArrowRight, Gift, Loader2 } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

const REWARD_IMG =
  "https://static.prod-images.emergentagent.com/jobs/f016b904-7317-4dc9-bed9-f4b71ecc7608/images/a982fb838156f9314c3b52d55de62c643fa83c9a2d0cb1310e7851e9bde5e8e8.png";

/**
 * Friend (referee) landing page.
 *
 * Ask for phone + email only — these are the contact details the friend will
 * use when they sign up on MYDAWA. MYDAWA owns phone verification during
 * signup, so we do NOT run a second OTP here (avoids friction + duplication).
 */
export default function FriendLanding() {
  const { slug } = useParams();
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkErr, setLinkErr] = useState("");

  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/link/${slug}`);
        setLink(data);
      } catch (e) {
        setLinkErr(formatApiError(e.response?.data?.detail) || "Link is invalid or expired.");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const fpData = await getDeviceFingerprint();
      const { data } = await api.post("/public/capture", {
        slug, phone, email,
        device_fp: fpData.fp,
        device_meta: fpData.meta,
      });
      setResult(data);
      fireConfetti();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Unable to continue");
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = async () => {
    if (!result?.promo_code) return;
    await navigator.clipboard.writeText(result.promo_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#66cc33]" />
      </div>
    );
  }
  if (linkErr) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#fafcfa] px-5 text-center">
        <MydawaLogo />
        <h1 className="mt-6 text-2xl font-extrabold">Link unavailable</h1>
        <p className="mt-2 max-w-sm text-sm text-[#5c665a]">{linkErr}</p>
        <a href="https://mydawa.com" className="btn-pill-primary mt-6">
          Go to MYDAWA <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    );
  }

  const { referrer_name, campaign } = link;

  return (
    <div className="min-h-screen bg-[#fafcfa]">
      <header className="border-b border-[#e2e8e2] bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <MydawaLogo />
          <span className="badge-green">Invite</span>
        </div>
      </header>

      <section className="mx-auto max-w-lg px-5 py-8 sm:py-12">
        {!result ? (
          <div className="anim-fade-up space-y-6">
            <div className="flex flex-col items-center text-center">
              <img src={REWARD_IMG} alt="Reward" className="h-40 w-40 object-contain" />
              <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#fff0f5] px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#c01974]">
                <Gift className="h-3.5 w-3.5" />
                Exclusive Invite
              </span>
              <h1 className="mt-4 text-3xl font-extrabold leading-tight text-[#1a1c19] sm:text-4xl">
                {referrer_name} invited you <br /> to MYDAWA
              </h1>
              {campaign.two_sided_mode ? (
                <p className="mt-3 text-base text-[#5c665a]">
                  Get{" "}
                  <strong className="text-[#ff3399]">
                    {campaign.discount_percent}% off
                  </strong>{" "}
                  (max KES {campaign.discount_max_kes}) on your first order.
                </p>
              ) : (
                <p className="mt-3 text-base text-[#5c665a]">
                  Enjoy fast, safe healthcare delivery from Kenya's most
                  trusted online pharmacy.
                </p>
              )}
            </div>

            <div className="rounded-xl border border-[#e2e8e2] bg-white px-4 py-3 text-xs text-[#5c665a]" data-testid="friend-capture-helper">
              Use the phone number and email you'll use to register on <strong className="text-[#1a1c19]">MYDAWA</strong>. We'll send your promo code here and MYDAWA will verify your phone when you sign up — no extra step on this page.
            </div>

            <form onSubmit={submit} className="space-y-3" data-testid="friend-capture-form">
              <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5c665a]">
                Phone number (for MYDAWA signup)
              </label>
              <input
                type="tel"
                inputMode="tel"
                placeholder="+254 7xx xxx xxx"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="input-lg"
                required
                data-testid="friend-phone-input"
              />
              <label className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5c665a]">
                Email (for MYDAWA signup)
              </label>
              <input
                type="email"
                inputMode="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-lg"
                required
                data-testid="friend-email-input"
              />
              {err && (
                <div className="rounded-lg bg-[#fff0f5] px-4 py-2 text-sm font-medium text-[#c01974]" data-testid="friend-error">
                  {err}
                </div>
              )}
              <button type="submit" disabled={submitting} className="btn-pill-primary w-full" data-testid="friend-continue-btn">
                {submitting ? "Please wait…" : (campaign.two_sided_mode ? "Claim my discount" : "Continue to MYDAWA")}
                <ArrowRight className="h-4 w-4" />
              </button>
            </form>

            <p className="text-center text-xs text-[#5c665a]">
              POM (prescription) items excluded. Your phone is only used for attribution and to deliver your code.
            </p>
          </div>
        ) : (
          <ResultCard
            result={result}
            referrer={referrer_name}
            campaign={campaign}
            copied={copied}
            onCopy={copyCode}
          />
        )}
      </section>
    </div>
  );
}

function ResultCard({ result, referrer, campaign, copied, onCopy }) {
  return (
    <div className="anim-fade-up space-y-5 text-center">
      <div className="flex justify-center">
        <img src={REWARD_IMG} alt="Reward" className="h-32 w-32 object-contain" />
      </div>
      {result.promo_code ? (
        <>
          <h2 className="text-2xl font-extrabold text-[#1a1c19]">
            You just unlocked{" "}
            <span className="text-[#ff3399]">
              {campaign.discount_percent}% off
            </span>
          </h2>
          <p className="text-sm text-[#5c665a]">
            Your exclusive promo code — valid for {campaign.code_expiry_days}{" "}
            days on any non-POM order.
          </p>
          <div className="rounded-2xl border-2 border-dashed border-[#ff3399] bg-[#fff0f5] p-5">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#c01974]">
              your promo code
            </div>
            <div
              className="mt-2 font-mono text-3xl font-extrabold tracking-widest text-[#1a1c19] sm:text-4xl"
              data-testid="friend-promo-code"
            >
              {result.promo_code}
            </div>
            <button onClick={onCopy} className="btn-outline-pill mt-3 w-full" data-testid="copy-code-btn">
              {copied ? (
                <>
                  <Check className="h-4 w-4 text-[#66cc33]" /> Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" /> Copy code
                </>
              )}
            </button>
          </div>
          <div className="rounded-xl bg-[#eaf8de] p-3 text-xs text-[#3f7c21]">
            <FaWhatsapp className="mr-1 inline h-4 w-4" />
            We've also sent your code via WhatsApp as backup.
          </div>
        </>
      ) : (
        <>
          <h2 className="text-2xl font-extrabold">Thanks! Your code is on the way.</h2>
          <p className="text-sm text-[#5c665a]">
            We're preparing your discount code and will send it to your
            WhatsApp within 24 hours.
          </p>
        </>
      )}
      <a
        href="https://mydawa.com"
        className="btn-pill-primary w-full"
        data-testid="continue-to-mydawa-btn"
      >
        Continue to MYDAWA <ArrowRight className="h-4 w-4" />
      </a>
    </div>
  );
}

function fireConfetti() {
  const colors = ["#66CC33", "#FF3399", "#FFB84D", "#5BB82E"];
  for (let i = 0; i < 50; i++) {
    const el = document.createElement("div");
    el.className = "confetti";
    el.style.left = Math.random() * 100 + "%";
    el.style.background = colors[Math.floor(Math.random() * colors.length)];
    el.style.animationDuration = 1.5 + Math.random() * 1.5 + "s";
    el.style.animationDelay = Math.random() * 0.3 + "s";
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 3000);
  }
}
